/* Native Shopify forms own consent. This module only controls visitor UI. */
const VISITOR = 'nymara:newsletter:visitor:v1';
const SESSION = 'nymara:newsletter:session:v1';

function read(store, key) {
  try { return JSON.parse(window[store].getItem(key) || '{}'); }
  catch { return {}; }
}

function write(store, key, value) {
  try {
    window[store].setItem(key, JSON.stringify(value));
    return true;
  } catch { return false; }
}

export function newsletterConfig() {
  const element = document.querySelector('[data-nymara-newsletter-config]');
  if (!element) throw new Error('Central newsletter configuration is missing');
  return JSON.parse(element.textContent);
}

export function rememberNewsletterSignup() {
  const value = read('localStorage', VISITOR);
  // No email address is persisted. This flag is not Shopify marketing consent.
  write('localStorage', VISITOR, { ...value, signup: true });
  write('sessionStorage', VISITOR, { signup: true });
  window.dispatchEvent(new Event('nymara:newsletter:signup'));
}

function observeSignup() {
  if (newsletterConfig().subscribed || document.querySelector('[data-nymara-newsletter-success]')) {
    rememberNewsletterSignup();
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeSignup, { once: true });
else observeSignup();
document.addEventListener('shopify:section:load', observeSignup);

export class NewsletterController {
  constructor(element, open) {
    this.element = element;
    this.open = open;
    this.config = newsletterConfig();
    this.abort = new AbortController();
    this.lastTick = performance.now();
    this.active = this.isActive();
    this.lastActiveAt = Date.now();
    const session = read('sessionStorage', SESSION);
    this.elapsed = Date.now() - (session.at || 0) < this.config.session_idle_minutes * 60000
      ? Math.max(0, Number(session.ms) || 0) : 0;
    // If persistence is blocked, retain inline signup without repeatedly interrupting visitors.
    this.persistent = write('localStorage', VISITOR, read('localStorage', VISITOR)) &&
      write('sessionStorage', SESSION, { ms: this.elapsed, at: Date.now() });
    const options = { signal: this.abort.signal };
    this.tick = this.tick.bind(this);
    for (const event of ['focus', 'blur', 'pageshow', 'pagehide', 'storage', 'nymara:newsletter:signup']) {
      window.addEventListener(event, this.tick, options);
    }
    document.addEventListener('visibilitychange', this.tick, options);
    document.addEventListener('theme4:age:verified', this.tick, options);
    document.addEventListener('submit', event => {
      if (event.target?.querySelector('input[name="form_type"][value="customer"]')) this.signupPending = true;
    }, { ...options, capture: true });
    if (this.persistent && !this.suppressed()) this.timer = setInterval(this.tick, 250);
    const formResponse = element.hasAttribute('initial-form-open') &&
      location.hash === '#' + element.dataset.formId;
    // Show the actual server response, including errors, even after dismissing the popup.
    if (formResponse) {
      this.shown = true;
      clearInterval(this.timer);
      this.open();
    }
    if (element.querySelector('[data-nymara-newsletter-success]')) rememberNewsletterSignup();
    this.tick();
  }

  isActive() {
    return document.visibilityState === 'visible' && document.hasFocus();
  }

  suppressed() {
    const visitor = read('localStorage', VISITOR);
    const legacy = read('localStorage', 'theme4:newsletter_popup');
    return this.config.subscribed || visitor.signup || read('sessionStorage', VISITOR).signup ||
      Date.now() < (visitor.nextShowAt || 0) || Date.now() < (legacy.expires || 0);
  }

  available() {
    const age = document.getElementById('hdt-age-modal');
    const focus = document.activeElement;
    return (!age || age.hasAttribute('is-verified')) &&
      !document.querySelector('dialog[open]') &&
      !(focus && focus.matches('input, textarea, select, [contenteditable="true"]'));
  }

  tick(event) {
    const now = performance.now();
    if (this.active) {
      if (!this.shown) this.elapsed += Math.max(0, now - this.lastTick);
      this.lastActiveAt = Date.now();
    } else if (this.isActive() && Date.now() - this.lastActiveAt >= this.config.session_idle_minutes * 60000) {
      this.elapsed = 0;
      this.lastActiveAt = Date.now();
    }
    this.lastTick = now;
    this.active = event?.type !== 'pagehide' && this.isActive();
    if (this.active || event?.type === 'pagehide' || event?.type === 'visibilitychange') {
      write('sessionStorage', SESSION, { ms: this.elapsed, at: Date.now() });
    }
    if (this.shown || this.suppressed()) clearInterval(this.timer);
    if (this.shown || this.signupPending || !this.persistent || !this.active || this.suppressed() ||
        this.elapsed < this.config.delay_seconds * 1000 || !this.available()) return;
    const show = () => {
      // Recheck after obtaining the cross-tab lock.
      if (this.shown || this.suppressed() || !this.active || !this.available()) return;
      this.shown = true;
      clearInterval(this.timer);
      write('localStorage', VISITOR, { ...read('localStorage', VISITOR),
        nextShowAt: Date.now() + this.config.dismiss_days * 86400000 });
      this.open();
    };
    if (navigator.locks) {
      if (this.pending) return;
      this.pending = true;
      navigator.locks.request('nymara-newsletter-popup', show).finally(() => { this.pending = false; });
    } else show();
  }

  disconnect() {
    this.tick({ type: 'pagehide' });
    this.abort.abort();
    clearInterval(this.timer);
  }
}

window.NymaraNewsletterController = NewsletterController;
window.dispatchEvent(new Event('nymara:newsletter:ready'));
